import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { 
    useGetSocialUserQuery,
    useApproveSocialRequestMutation,
    useDeclineSocialRequestMutation
} from "../../../../../redux/apiSlice";
import { SocialIcon } from "../../../../util/Icons";
import { formatStatus, formatDate } from "../../../../util/Helpers";
import cogoToast from "cogo-toast";


const ViewRequestDetailsModal = ({ showViewRequestDetailsModal, details, onHide }) => {
  const { isFetching, data, error } = useGetSocialUserQuery(details?.userId || details?.socialUserId);
  
  const [approveSocialRequest, { isLoading: isApproving }] = useApproveSocialRequestMutation();
  const [declineSocialRequest, { isLoading: isDecliningRequest }] = useDeclineSocialRequestMutation();

  const [isDeclining, setIsDeclining] = useState(false);
  const [statusComment, setStatusComment] = useState("");

  const handleApprove = () => {
      approveSocialRequest({
          requestId: details?.requestId
      })
      .unwrap()
      .then(() => {
          cogoToast.success("Request Approved Successfully");
          onHide();
      })
      .catch((err) => {
          cogoToast.error("Failed to approve request");
      });
  };

  const handleDecline = () => {
      if (!statusComment.trim()) {
          cogoToast.error("Please provide a reason for declining");
          return;
      }
      declineSocialRequest({
          requestId: details?.requestId,
          statusComment: statusComment
      })
      .unwrap()
      .then(() => {
          cogoToast.success("Request Declined Successfully");
          onHide();
      })
      .catch((err) => {
          cogoToast.error("Failed to decline request");
      });
  };

  const cancelDecline = () => {
      setIsDeclining(false);
      setStatusComment("");
  };

  return (
    <Modal
      show={showViewRequestDetailsModal}
      onHide={onHide}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter" className="d-flex align-items-center">
          <span className="svg-icon svg-icon-lg svg-icon-primary mr-3">
            <SocialIcon />
          </span>
          <span className="font-weight-bold">Social Profile Details</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0" style={{ minHeight: "400px" }}>
        {isFetching ? (
          <div
            className="d-flex align-items-center justify-content-center flex-column"
            style={{ height: "400px" }}
          >
            <div className="spinner spinner-primary spinner-lg mb-4"></div>
            <h6 className="text-muted">Loading social profile...</h6>
          </div>
        ) : error ? (
          <div
            className="d-flex align-items-center justify-content-center flex-column"
            style={{ height: "400px" }}
          >
            <span className="svg-icon svg-icon-4x svg-icon-danger mb-4">
               <i className="flaticon2-warning font-size-h1 text-danger"></i>
            </span>
            <h6 className="text-danger">Error loading profile data</h6>
            <p className="text-muted small">Please try again later.</p>
          </div>
        ) : (
          <div className="p-8">
             {/* Profile Header */}
             <div className="d-flex align-items-center mb-8">
                <div className="symbol symbol-60 symbol-light-primary mr-5">
                    <span className="symbol-label font-size-h2 font-weight-bold text-primary">
                        {data?.data?.displayName?.[0]?.toUpperCase() || "U"}
                    </span>
                </div>
                <div>
                    <div className="font-weight-bolder font-size-h4 text-dark-75">
                        {data?.data?.displayName || "N/A"}
                    </div>
                    <div className="text-muted font-size-lg">
                        @{data?.data?.username || "N/A"}
                    </div>
                </div>
                <div className="ml-auto">
                    {formatStatus(data?.data?.status)}
                </div>
            </div>

            <div className="separator separator-dashed my-8"></div>

            {/* Account Details */}
            <h6 className="font-weight-bolder text-dark mb-4">Account Details</h6>
            <div className="row mb-8">
                <div className="col-md-6 mb-4">
                    <span className="text-muted font-weight-bold d-block mb-1">User Level</span>
                    <span className="font-weight-bolder font-size-lg text-dark">{data?.data?.userLevel || "N/A"}</span>
                </div>
                <div className="col-md-6 mb-4">
                    <span className="text-muted font-weight-bold d-block mb-1">Profile Created</span>
                    <span className="font-weight-bolder font-size-lg text-dark">{formatDate(data?.data?.created)}</span>
                </div>
            </div>

            <div className="separator separator-dashed my-8"></div>

            {/* Social Stats */}
            <h6 className="font-weight-bolder text-dark mb-4">Social Stats</h6>
            <div className="row">
                 <div className="col-md-3 col-6 mb-4 text-center">
                    <div className="bg-light-primary rounded p-4">
                        <div className="text-primary font-weight-bolder font-size-h3 mb-1">{data?.data?.followerCount || 0}</div>
                        <div className="text-muted font-weight-bold font-size-sm text-uppercase">Followers</div>
                    </div>
                 </div>
                 <div className="col-md-3 col-6 mb-4 text-center">
                    <div className="bg-light-info rounded p-4">
                        <div className="text-info font-weight-bolder font-size-h3 mb-1">{data?.data?.followsCount || 0}</div>
                        <div className="text-muted font-weight-bold font-size-sm text-uppercase">Following</div>
                    </div>
                 </div>
                 <div className="col-md-3 col-6 mb-4 text-center">
                    <div className="bg-light-success rounded p-4">
                        <div className="text-success font-weight-bolder font-size-h3 mb-1">{data?.data?.postCount || 0}</div>
                        <div className="text-muted font-weight-bold font-size-sm text-uppercase">Posts</div>
                    </div>
                 </div>
                  <div className="col-md-3 col-6 mb-4 text-center">
                    <div className="bg-light-warning rounded p-4">
                        <div className="text-warning font-weight-bolder font-size-h3 mb-1">{data?.data?.transactionCount || 0}</div>
                        <div className="text-muted font-weight-bold font-size-sm text-uppercase">Transactions</div>
                    </div>
                 </div>
            </div>
            
            {isDeclining && (
                <div className="mt-8 bg-light-danger rounded p-4">
                    <h6 className="text-danger font-weight-bold mb-3">Reason for Declining</h6>
                    <Form.Control 
                        as="textarea" 
                        rows={3} 
                        placeholder="Enter reason..." 
                        value={statusComment}
                        onChange={(e) => setStatusComment(e.target.value)}
                        className="form-control-solid"
                    />
                </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!isDeclining ? (
            <>
                <Button variant="light" onClick={onHide} disabled={isApproving || isDecliningRequest}>
                    Close
                </Button>
                <Button variant="light-danger" onClick={() => setIsDeclining(true)} disabled={isApproving || isDecliningRequest}>
                    Decline Request
                </Button>
                <Button variant="primary" onClick={handleApprove} disabled={isApproving || isDecliningRequest}>
                    {isApproving ? "Approving..." : "Approve Request"}
                </Button>
            </>
        ) : (
            <>
                <Button variant="light" onClick={cancelDecline} disabled={isDecliningRequest}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={handleDecline} disabled={isDecliningRequest}>
                    {isDecliningRequest ? "Declining..." : "Confirm Decline"}
                </Button>
            </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ViewRequestDetailsModal;
